import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Shield, Heart, Star } from 'lucide-react';
import { api } from '../context/AuthContext';
import ProductCard from '../components/ProductCard';
import ProducerCard from '../components/ProducerCard';
import { Button } from '../components/ui/button';
import { useSEO } from '../hooks/useSEO';

const SITE_URL = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');

const HERO_DEFAULTS = {
  hero_image_url: null,   // null = nessuna immagine default; il CMS la fornisce o si usa gradient puro
  hero_title: 'I Sapori Autentici del Sud Italia',
  hero_subtitle: 'Scopri prodotti selezionati direttamente dai migliori produttori artigianali. Qualità, tradizione e territorio in ogni acquisto.',
  hero_cta_primary_text: 'Esplora i Prodotti',
  hero_cta_primary_url: '/prodotti',
  hero_cta_secondary_text: 'Conosci i Produttori',
  hero_cta_secondary_url: '/produttori',
};

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [featuredProducers, setFeaturedProducers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cmsConfig, setCmsConfig] = useState(HERO_DEFAULTS);

  useSEO({
    canonicalPath: '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: `${SITE_URL}/`,
          name: 'Dal Sud',
          description: 'Marketplace di prodotti artigianali e alimentari del Sud Italia',
          inLanguage: 'it-IT',
          potentialAction: {
            '@type': 'SearchAction',
            target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/prodotti?search={search_term_string}` },
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: 'Dal Sud',
          url: `${SITE_URL}/`,
          description: 'Marketplace italiano che connette produttori artigianali del Sud Italia con acquirenti in tutta Italia.',
          areaServed: { '@type': 'Country', name: 'Italia' },
        },
      ],
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, producersRes, cmsRes] = await Promise.all([
          api.get('/products/featured?limit=8'),
          api.get('/producers/featured?limit=6'),
          api.get('/cms/homepage'),
        ]);
        setFeaturedProducts(productsRes.data);
        setFeaturedProducers(producersRes.data);
        // Merge CMS values over defaults (only non-null values override)
        setCmsConfig(prev => {
          const merged = { ...prev };
          for (const [k, v] of Object.entries(cmsRes.data)) {
            if (v !== null && v !== undefined && v !== '') merged[k] = v;
          }
          return merged;
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] max-h-[700px] overflow-hidden">
        <div className="absolute inset-0">
          {cmsConfig.hero_image_url ? (
            <>
              <img
                src={cmsConfig.hero_image_url}
                alt="Hero Dal Sud"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary via-primary/80 to-secondary" />
          )}
        </div>

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16 md:pb-20">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-3xl leading-tight tracking-tight">
            {cmsConfig.hero_title}
          </h1>
          <p className="mt-4 text-lg md:text-xl text-white/90 max-w-2xl">
            {cmsConfig.hero_subtitle}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Button size="lg" asChild className="text-base" data-testid="hero-cta-products">
              <Link to={cmsConfig.hero_cta_primary_url}>
                {cmsConfig.hero_cta_primary_text}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="text-base bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="hero-cta-producers">
              <Link to={cmsConfig.hero_cta_secondary_url}>
                {cmsConfig.hero_cta_secondary_text}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Produttori Selezionati</h3>
                <p className="mt-1 text-text-secondary text-sm">
                  Ogni produttore è verificato e selezionato per garantire qualità e autenticità.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Spedizione Diretta</h3>
                <p className="mt-1 text-text-secondary text-sm">
                  Dal produttore a casa tua, per mantenere freschezza e qualità.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Supporto Locale</h3>
                <p className="mt-1 text-text-secondary text-sm">
                  Ogni acquisto sostiene direttamente le piccole realtà del Sud Italia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary">
                Prodotti in Evidenza
              </h2>
              <p className="mt-2 text-text-secondary">
                Le eccellenze selezionate dalla nostra redazione
              </p>
            </div>
            <Link to="/prodotti" className="hidden md:flex items-center gap-2 text-primary font-medium hover:underline" data-testid="view-all-products">
              Vedi tutti
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2 mt-2" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {featuredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-background rounded-xl">
              <p className="text-text-secondary">Nessun prodotto in evidenza al momento</p>
            </div>
          )}

          <Link to="/prodotti" className="mt-6 md:hidden flex items-center justify-center gap-2 text-primary font-medium">
            Vedi tutti i prodotti
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Featured Producers */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary">
                I Nostri Produttori
              </h2>
              <p className="mt-2 text-text-secondary">
                Artigiani e agricoltori che portano avanti tradizioni centenarie
              </p>
            </div>
            <Link to="/produttori" className="hidden md:flex items-center gap-2 text-primary font-medium hover:underline" data-testid="view-all-producers">
              Vedi tutti
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                  <div className="w-24 h-24 mx-auto bg-gray-200 rounded-full" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mt-4" />
                </div>
              ))}
            </div>
          ) : featuredProducers.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
              {featuredProducers.map(producer => (
                <ProducerCard key={producer.id} producer={producer} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <p className="text-text-secondary">Nessun produttore in evidenza al momento</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-primary">
            <div className="absolute inset-0 opacity-10">
              <img
                src="https://images.pexels.com/photos/39579/tomato-red-fruit-vegetables-39579.jpeg"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative px-8 py-12 md:px-12 md:py-16 text-center">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
                Sei un Produttore?
              </h2>
              <p className="mt-4 text-lg text-white/90 max-w-2xl mx-auto">
                Unisciti a DalSud e porta i tuoi prodotti a migliaia di clienti appassionati 
                di qualità e autenticità. Commissioni trasparenti e supporto dedicato.
              </p>
              <Button size="lg" variant="secondary" asChild className="mt-8 bg-white text-primary hover:bg-white/90" data-testid="cta-become-producer">
                <Link to="/diventa-produttore">
                  Diventa Produttore
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
